import React from "react";
import { useNavigate } from "react-router-dom";
import SlipMark from "../assets/slip-mark-light.svg";
import SlipMarkDark from "../assets/slip-mark-dark.svg";
import ThemeToggle from "../components/ThemeToggle";
import menu from "../assets/menu.svg";

// Constants
const LINKS = {
    PLAYGROUND: 'https://playground.tryonlabs.ai',
    DOCS: 'https://docs.tryonlabs.ai',
    GITHUB: 'https://github.com/tryonlabs/',
    DEVELOPERS: '/developers'
};

const Navbar = () => {
    return (
        <nav role="navigation" aria-label="Main navigation" className="fixed top-0 z-40 bg-ground h-auto w-screen sm:w-full border-b border-hairline">
            <div className="mx-auto max-w-7xl flex items-center justify-between p-2 sm:px-4">
                <div className="flex items-center mr-6">
                    <div className="flex items-center cursor-pointer" onClick={() => window.location.href = "/"}>
                        <img className="nav-menu w-6 h-6 object-contain cursor-pointer mr-2 md:hidden" src={menu} alt="menu" loading="lazy" />
                        <img className="mark-on-light w-8 h-8 shrink-0 object-contain" src={SlipMark} alt="" loading="lazy" />
                        <img className="mark-on-dark w-8 h-8 shrink-0 object-contain" src={SlipMarkDark} alt="" loading="lazy" />
                        <span className="hidden md:block ml-3 text-xl font-bold text-ink">TryOn AI</span>
                    </div>
                </div>

                <div className="flex items-center justify-center whitespace-nowrap md:w-max pointer-events-auto gap-0.5 sm:gap-2 md:gap-3">
                    <ThemeToggle />
                    <a target="_blank" href={LINKS.GITHUB} rel="noopener noreferrer" className="hidden md:flex items-center justify-center w-10 h-8 text-muted hover:text-ink transition-colors">
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                        </svg>
                    </a>
                    <a href={LINKS.DEVELOPERS}>
                        <button className="flex items-center justify-center whitespace-nowrap md:w-max md:py-2 sm:py-1.5 py-1.5 px-2 sm:px-3 md:px-4 font-medium sm:text-xs text-xs leading-4 not-italic text-coral border border-coral-line rounded-full hover:bg-tray transition-colors bg-sheet">
                            For Developers
                        </button>
                    </a>
                    <a target="_blank" href={LINKS.DOCS} rel="noopener noreferrer">
                        <button className="flex items-center justify-center whitespace-nowrap md:w-max md:py-2 sm:py-1.5 py-1.5 px-2 sm:px-3 md:px-4 font-medium sm:text-xs text-xs leading-4 not-italic text-coral border border-coral-line rounded-full hover:bg-tray transition-colors bg-sheet">
                            Docs
                        </button>
                    </a>
                    <a target="_blank" href="https://playground.tryonlabs.ai/pricing" rel="noopener noreferrer">
                        <button className="hidden sm:flex items-center justify-center whitespace-nowrap md:w-max md:py-2 sm:py-1.5 py-1.5 px-2 sm:px-3 md:px-4 font-medium sm:text-xs text-xs leading-4 not-italic text-coral border border-coral-line rounded-full hover:bg-tray transition-colors bg-sheet">
                            Pricing
                        </button>
                    </a>
                    <a href="https://playground.tryonlabs.ai/credits" target="_blank" rel="noopener noreferrer">
                        <button className="flex items-center justify-center whitespace-nowrap md:w-max md:py-2 sm:py-1.5 py-1.5 px-2 sm:px-3 md:px-4 font-medium sm:text-xs text-xs leading-4 not-italic text-white bg-action rounded-full hover:bg-action-hover transition-colors shadow-[0_12px_28px_-10px_rgba(190,49,32,0.65)]">
                            Get API Access
                        </button>
                    </a>
                </div>
            </div>
        </nav>
    );
};

export default function NotFound() {
  const navigate = useNavigate();

  return (
    <div className="h-full overflow-hidden w-full bg-ground">
      <Navbar />
      <div className="h-full scrollbar items-center justify-start bg-ground pt-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="pt-12 pb-20">
            <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
              {/* Large 404 with gradient effect */}
              <div className="mb-6 relative">
                <h1 className="text-6xl md:text-8xl font-newsreader font-medium mb-1">
                  <span className="text-ink">
                    404
                  </span>
                </h1>
              </div>

              {/* Main heading */}
              <h2 className="text-3xl md:text-4xl font-bold text-ink mb-4">
                Page Not Found
              </h2>

              {/* Description text */}
              <div className="mb-8 max-w-xl">
                <p className="text-lg text-muted mb-2 font-medium">
                  Oops! The page you're looking for doesn't exist.
                </p>
                <p className="text-sm text-muted">
                  The URL you entered may be incorrect or the page may have been
                  moved. Don't worry, let's get you back on track!
                </p>
              </div>

              {/* Main action card */}
              <div className="bg-sheet rounded-2xl border border-hairline p-8 mb-8 max-w-md w-full transform transition-all">
                <div className="flex items-center justify-center mb-6">
                  <div className="relative">
                    <div className="absolute inset-0 bg-tray rounded-full opacity-50 border border-hairline"></div>
                    <div className="relative rounded-full p-4 border bg-tray border-hairline">
                      <svg
                        className="w-12 h-12 text-ink"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    </div>
                  </div>
                </div>

                <h3 className="text-xl font-semibold text-ink mb-3">
                  Let's Get You Back
                </h3>
                <p className="text-muted mb-6 leading-relaxed">
                  Return to the home page to continue exploring our AI-powered
                  fashion services including virtual try-on, model generation, and
                  more.
                </p>

                <button
                  onClick={() => navigate("/")}
                  className="w-full px-6 py-3 text-white font-semibold rounded-full transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-2 bg-action hover:bg-action-hover shadow-[0_12px_28px_-10px_rgba(190,49,32,0.65)]"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                    />
                  </svg>
                  Go to Home Page
                </button>
              </div>

              {/* Quick links */}
              <div className="flex flex-wrap items-center justify-center gap-4 text-sm">
                <div className="flex items-center gap-2 text-muted">
                  <svg
                    className="w-4 h-4 text-muted"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <span>Need help?</span>
                </div>
                <a
                  href="/contact-us"
                  className="text-ink hover:text-coral font-medium transition-colors flex items-center gap-1 group"
                >
                  <span>Contact us</span>
                  <svg
                    className="w-4 h-4 transform group-hover:translate-x-1 transition-transform"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </a>
              </div>

            </div>

            {/* Decorative elements */}
            <div className="fixed top-20 left-10 w-32 h-32 bg-tray rounded-full opacity-30 -z-10 pointer-events-none border border-hairline"></div>
            <div className="fixed bottom-20 right-10 w-40 h-40 bg-tray rounded-full opacity-30 -z-10 pointer-events-none border border-hairline"></div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

// Footer Component
const Footer = () => (
    <div className="w-full py-16 bg-ground border-t border-hairline">
        <div className="max-w-6xl mx-auto px-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-12">
                {/* Company */}
                <div>
                    <h4 className="text-ink font-semibold mb-4">TryOn AI</h4>
                    <p className="text-muted text-sm leading-relaxed mb-4">
                        Ready to Build the Future? <br/> Join the revolution in fashion AI.
                    </p>
                    <div className="flex space-x-4">
                        <a href="mailto:contact@tryonlabs.ai" className="text-muted hover:text-coral transition-colors">
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M3 8l9 6 9-6v10a2 2 0 01-2 2H5a2 2 0 01-2-2V8z"/>
                                <path d="M21 6L12 13 3 6h18z"/>
                            </svg>
                        </a>
                    </div>
                </div>

                {/* Playground */}
                <div>
                    <h4 className="text-ink font-semibold mb-4">Playground</h4>
                    <ul className="space-y-2 text-sm">
                        <li><a href="https://playground.tryonlabs.ai" target="_blank" rel="noopener noreferrer" className="text-muted hover:text-coral transition-colors">Live Playground</a></li>
                        <li><a href="https://playground.tryonlabs.ai/pricing" target="_blank" rel="noopener noreferrer" className="text-muted hover:text-coral transition-colors">Pricing</a></li>
                        <li><a href="https://docs.tryonlabs.ai" target="_blank" rel="noopener noreferrer" className="text-muted hover:text-coral transition-colors">Documentation</a></li>
                        <li><a href="mailto:support@tryonlabs.ai" className="text-muted hover:text-coral transition-colors">Support</a></li>
                    </ul>
                </div>

                {/* APIs */}
                <div>
                    <h4 className="text-ink font-semibold mb-4">APIs</h4>
                    <ul className="space-y-2 text-sm">
                        <li><a href="https://docs.tryonlabs.ai/api-playground/virtual-try-on" target="_blank" rel="noopener noreferrer" className="text-muted hover:text-coral transition-colors">Virtual Try-On</a></li>
                        <li><a href="https://docs.tryonlabs.ai/outfit-generation" target="_blank" rel="noopener noreferrer" className="text-muted hover:text-coral transition-colors">AI Outfit Generation</a></li>
                        <li><a href="https://docs.tryonlabs.ai/outfit-generation" target="_blank" rel="noopener noreferrer" className="text-muted hover:text-coral transition-colors">Outfit Generation</a></li>
                        <li><a href="https://docs.tryonlabs.ai/outfit-captioning" target="_blank" rel="noopener noreferrer" className="text-muted hover:text-coral transition-colors">Outfit Captioning</a></li>
                    </ul>
                </div>

                {/* Company */}
                <div>
                    <h4 className="text-ink font-semibold mb-4">Company</h4>
                    <ul className="space-y-2 text-sm">
                        <li><a href="/" className="text-muted hover:text-coral transition-colors">Home</a></li>
                        <li><a href="https://github.com/tryonlabs/" target="_blank" rel="noopener noreferrer" className="text-muted hover:text-coral transition-colors">Open Source</a></li>
                        <li><a href="mailto:contact@tryonlabs.ai?subject=Partnership" className="text-muted hover:text-coral transition-colors">Partnerships</a></li>
                        <li><a href="/contact-us" className="text-muted hover:text-coral transition-colors">Contact Us</a></li>
                        <li><a href="/terms-and-conditions" className="text-muted hover:text-coral transition-colors">Terms & Conditions</a></li>
                        <li><a href="/cancellation-and-refund-policy" className="text-muted hover:text-coral transition-colors">Cancellation & Refund Policy</a></li>
                    </ul>
                </div>
            </div>

            {/* Bottom Bar */}
            <div className="pt-8 border-t border-hairline flex flex-col md:flex-row justify-between items-center">
                <p className="text-muted text-sm">
                    &copy; {new Date().getFullYear()} TryOn AI. All rights reserved.
                </p>
                <div className="flex items-center space-x-6 mt-4 md:mt-0">
                    <span className="text-muted text-sm">Status:</span>
                    <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-ink rounded-full"></div>
                        <span className="text-ink text-sm">All APIs Operational</span>
                    </div>
                </div>
            </div>
        </div>
    </div>
);
